import React from 'react';
import { gql } from '@apollo/client';

import { messageFromError } from '@oysterjs/core/graphql/errors';
import { client } from '~/api/client';
import {
  type CaraWorkspaceArtifactEmailInput,
  CaraWorkspaceArtifactType,
  CaraWorkspaceState,
  CreateMailboxDraftDocument,
  type CreateMailboxDraftMutation,
  type CreateMailboxDraftMutationVariables,
  FileType,
  type MailboxAccountFieldsFragment,
  MailboxAccountStatus,
  MailboxAccountsDocument,
  type MailboxDraftFieldsFragment,
  type MailboxAccountsQuery,
  type MailboxAccountsQueryVariables,
  MailboxComposeLimitsDocument,
  type MailboxComposeLimitsQuery,
  type MailboxComposeLimitsQueryVariables,
  type MailboxSignatureFieldsFragment,
  MailboxSignaturesDocument,
  type MailboxSignaturesQuery,
  type MailboxSignaturesQueryVariables,
  OutboundEmailStatus,
  SwitchMailboxDraftDocument,
  type SwitchMailboxDraftMutation,
  type SwitchMailboxDraftMutationVariables,
  SearchMailboxDraftsDocument,
  type SearchMailboxDraftsQuery,
  type SearchMailboxDraftsQueryVariables,
  SearchOutboundEmailsDocument,
  type SearchOutboundEmailsQuery,
  type SearchOutboundEmailsQueryVariables,
  ScheduleMailboxEmailDocument,
  type ScheduleMailboxEmailMutation,
  type ScheduleMailboxEmailMutationVariables,
  CancelMailboxEmailPreparationDocument,
  type CancelMailboxEmailPreparationMutation,
  type CancelMailboxEmailPreparationMutationVariables,
  CancelMailboxEmailDocument,
  type CancelMailboxEmailMutation,
  type CancelMailboxEmailMutationVariables,
  UpdateCaraWorkspaceArtifactDocument,
  type UpdateCaraWorkspaceArtifactMutation,
  type UpdateCaraWorkspaceArtifactMutationVariables,
  UploadEmailAttachmentDocument,
  type UploadEmailAttachmentMutation,
  type UploadEmailAttachmentMutationVariables
} from '~/api/graphql';
import {
  mailboxActionError,
  requiresMailboxRefresh,
  type MailboxActionError
} from '~/api/data/mailbox/errors';
import { mailClientName } from '~/lib/mailbox-providers';
import { LONGEST_TIMER_DELAY_MS } from '~/lib/scheduled-send';
import {
  ATTACHMENT_LIMIT_BYTES,
  resolveAttachmentLimit
} from '~/components/cara-input/cara-attachment';
import { isCommitted, isScheduled, useScheduledEmail } from './use-scheduled-email';

export type MailboxStatus = {
  mailboxDraft: MailboxDraftFieldsFragment | null;
  sentEmail: SentEmail | null;
  scheduledEmail: SentEmail | null;
};

/**
 * Decides which mailbox responses still count for the email on screen, and
 * holds the draft the last one reported.
 *
 * Mailbox reads are slower than the composer: a person can switch artifact, or
 * press send, while a draft lookup is still out. `active` retires every read
 * belonging to an email the composer has left; `generation` retires reads that
 * a mutation has overtaken, so an older empty list cannot erase the draft that
 * mutation just created; `pendingMutations` keeps the periodic refresh from
 * starting one mid-flight.
 */
export type MailboxRequestScope = {
  readonly active: boolean;
  mounted: boolean;
  generation: number;
  pendingMutations: number;
  draft: MailboxDraftFieldsFragment | null;
  send: SendAttempt | null;
};

/**
 * The click a cancellation may still reach. `id` is the key the server knows
 * this click by, so Cancel need not wait for the schedule response to learn an
 * outbound id that may not exist yet. `deadline` is measured from the click
 * rather than from the moment preparation finished, because the five seconds
 * belong to the person who pressed the button. `completed` says whether the
 * schedule request has returned, which is what decides whether an accepted
 * cancellation still has a request in flight behind it.
 */
type SendAttempt = {
  id: string;
  deadline: number;
  cancelled: boolean;
  started: boolean;
  completed: boolean;
};

// Both ways a switch can fail to report back -- a result without the payload
// and a thrown error -- leave the sender in the same place: the change may or
// may not have happened, so the advice has to be identical.
const SWITCH_UNCONFIRMED =
  'We could not confirm the mailbox change. Reload to check the current sender and draft before trying again.';

export type EmailComposerSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * How a send resolved, which is all the composer needs of it: enough to lock
 * the form and say so. Narrowed to the fields both the mutation and the send
 * log carry, because the composer learns about a send from either.
 */
export type SentEmail = Pick<
  NonNullable<ScheduleMailboxEmailMutation['scheduleMailboxEmail']>['email'],
  | 'id'
  | 'status'
  | 'sentAt'
  | 'sendAfter'
  | 'scheduledFor'
  | 'sentByUserId'
  | 'sentBy'
  | 'cancellationRemainingMs'
  | 'failureReason'
>;

/**
 * The send whose delivery the composer watches, and when that delivery can
 * start: a send in its Undo window or past it, or a scheduled email. Null when
 * nothing is on its way.
 */
const awaitedDelivery = (
  status: Pick<MailboxStatus, 'sentEmail' | 'scheduledEmail'>,
  undoDeadline?: string
): { id: string; startsAt: string | Date | null | undefined } | null => {
  if (status.sentEmail?.status === OutboundEmailStatus.Sending) {
    return { id: status.sentEmail.id, startsAt: undoDeadline };
  }
  if (status.scheduledEmail) {
    return { id: status.scheduledEmail.id, startsAt: status.scheduledEmail.scheduledFor };
  }
  return null;
};

/**
 * An uploaded attachment as the recipient will meet it: the name on the file
 * and the bytes that travel. The draft stores only the id, so anything the
 * composer wants to show about an attachment has to be carried alongside.
 */
export type AttachmentFile = {
  id: string;
  name: string;
  size: number;
};

/**
 * An upload still in flight. Keyed separately from the file's name because two
 * copies of the same document can be uploading at once, and the chip that
 * disappears must be the one whose upload finished.
 */
export type PendingUpload = {
  key: string;
  name: string;
  size: number;
};

/**
 * The server's own words for a refusal, or the caller's copy when the server
 * offered none. The client runs with `errorPolicy: 'all'`, so a refused mutation
 * resolves with a null field and its reason in `errors` rather than rejecting;
 * reading only the field turns every specific refusal into "please try again".
 */
const messageFromResult = (result: { errors?: readonly unknown[] }, fallback: string): string =>
  messageFromError({ graphQLErrors: result.errors ?? [] }, fallback);

/**
 * The artifact types the server can render into an attachment, mirrored from
 * `EmailArtifactAttachment`. Anything else attaches happily and then fails the
 * whole send, so the picker is never offered one.
 */
const ATTACHABLE_ARTIFACT_TYPES = [
  CaraWorkspaceArtifactType.DocumentHtml,
  CaraWorkspaceArtifactType.Document,
  CaraWorkspaceArtifactType.TemplateForm,
  CaraWorkspaceArtifactType.CustomForm,
  CaraWorkspaceArtifactType.PdfFile,
  CaraWorkspaceArtifactType.XlsxFile,
  CaraWorkspaceArtifactType.DocxFile
];

/**
 * How many documents the picker asks for. The list is searched rather than
 * paged, so this is the point past which another row helps nobody: a sender who
 * has scrolled fifty results is better served by typing two more letters.
 */
const ATTACHABLE_ARTIFACT_LIMIT = 50;

/**
 * The server's ceiling on IDs per call. Exceeded, it refuses outright rather
 * than answering in part, so a longer draft is asked about in batches.
 */
const ATTACHED_ARTIFACT_ID_LIMIT = 200;

/**
 * The largest composed email we will hand to a `keepalive` request. Browsers
 * cap the total in-flight `keepalive` body at 64 KiB, and the request carries
 * the GraphQL query and the rest of the input beside the email itself, so the
 * ceiling sits below that with room for the remainder. A larger email is sent
 * as an ordinary request, which the browser may abandon if the page goes away,
 * rather than being rejected outright for exceeding the cap.
 */
const KEEPALIVE_EMAIL_BODY_LIMIT_BYTES = 48_000;

const SEND_CANCELLATION_WINDOW_MS = 5_000;
// Covers a Cancel that overtakes Send on the network, not provider preparation.
const CANCELLATION_RETRY_MS = 200;
const DELIVERY_POLL_INITIAL_DELAY_MS = 2_000;
const DELIVERY_POLL_MAX_DELAY_MS = 30_000;
const DELIVERY_POLL_DURATION_MS = 5 * 60_000;

/**
 * Hand-written, with its filter type below, until `yarn generate` runs over
 * `SearchAttachableArtifacts` in cara-workspace.graphql; both give way to the
 * generated pair then.
 */
const searchAttachableArtifactsDocument = gql`
  query SearchAttachableArtifacts($filter: CaraWorkspaceArtifactFilter) {
    caraWorkspaceArtifacts(filter: $filter) {
      id
      title
      type
      updatedAt
      metadata {
        workspaceId
        workspaceTitle
        clientName
      }
    }
  }
`;

type AttachableArtifactFilter = {
  search?: string;
  limit?: number;
  ids?: string[];
  types?: CaraWorkspaceArtifactType[];
};

type SearchAttachableArtifactsQuery = {
  caraWorkspaceArtifacts: {
    id: string;
    title: string;
    type: CaraWorkspaceArtifactType;
    updatedAt: string;
    metadata: { workspaceId: string; workspaceTitle: string; clientName: string };
  }[];
};

/**
 * A document the composer can attach, flattened out of the metadata the server
 * nests it in. Carries where it came from because the picker offers artifacts
 * from every workspace the sender can read, and attaching another client's
 * document must never be something that happens by accident.
 */
export type AttachableArtifact = {
  id: string;
  title: string;
  type: string;
  workspaceId: string;
  workspaceTitle: string;
  clientName: string;
  updatedAt: string;
};

/**
 * Reads the artifact list and flattens it. Rejects rather than resolving empty
 * when the list does not arrive, so callers can tell "nothing matched" from
 * "the read failed" — the two want opposite things from the person reading the
 * screen.
 */
const readAttachableArtifacts = async (
  filter: AttachableArtifactFilter
): Promise<AttachableArtifact[]> => {
  const result = await client.query<
    SearchAttachableArtifactsQuery,
    { filter: AttachableArtifactFilter }
  >({
    query: searchAttachableArtifactsDocument,
    variables: { filter: { ...filter, types: ATTACHABLE_ARTIFACT_TYPES } },
    fetchPolicy: 'network-only'
  });
  const artifacts = result.data?.caraWorkspaceArtifacts;
  if (!artifacts) {
    throw new Error(messageFromResult(result, 'We could not load documents to attach.'));
  }
  return artifacts.map((artifact) => ({
    id: artifact.id,
    title: artifact.title,
    type: artifact.type,
    updatedAt: artifact.updatedAt,
    workspaceId: artifact.metadata.workspaceId,
    workspaceTitle: artifact.metadata.workspaceTitle,
    clientName: artifact.metadata.clientName
  }));
};

let pendingUploadCounter = 0;

/**
 * Every network call the email composer makes, in one place: the mailboxes and
 * signatures it can pick from, whether this draft has already gone out, the
 * draft write-back, and the send.
 *
 * Mailbox status also refreshes after Cara finishes work and before mailbox
 * actions: this composer can stay open while Cara creates a provider draft.
 */
export const useEmailComposer = (props: {
  workspaceId: string;
  artifactId: string;
  version: number;
  workspaceState?: CaraWorkspaceState;
  /** The revision of the composition this composer rendered. */
  contentRevision: string;
  /**
   * True while the focus rail is still fetching this version's body. When it
   * flips false, adopt `contentRevision` once — that is the stored fingerprint,
   * not a remote edit the sender has not read.
   */
  bodyHydrating?: boolean;
}) => {
  const { workspaceId, artifactId, version, contentRevision, workspaceState, bodyHydrating } =
    props;

  const [mailboxAccounts, setMailboxAccounts] = React.useState<MailboxAccountFieldsFragment[]>([]);
  const [signatures, setSignatures] = React.useState<MailboxSignatureFieldsFragment[]>([]);
  /**
   * The server's ceiling on body HTML, null until it arrives. Null means the
   * composer refuses nothing on size — the server refuses either way.
   */
  const [bodyLimitBytes, setBodyLimitBytes] = React.useState<number | null>(null);
  /**
   * Attachment ceiling, seeded with the fallback so a failed limits query still
   * refuses an oversized file instead of going quiet. Replaced when the server
   * answers with `mailboxComposeLimits.attachmentBytes`.
   */
  const [attachmentLimitBytes, setAttachmentLimitBytes] = React.useState(ATTACHMENT_LIMIT_BYTES);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [saveStatus, setSaveStatus] = React.useState<EmailComposerSaveStatus>('idle');
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const [uploads, setUploads] = React.useState<AttachmentFile[]>([]);
  const [uploading, setUploading] = React.useState<PendingUpload[]>([]);
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  const [savingToMailbox, setSavingToMailbox] = React.useState(false);
  const [mailboxDraftError, setMailboxDraftError] = React.useState<string | null>(null);
  const [loadingMailboxDraft, setLoadingMailboxDraft] = React.useState(true);
  /**
   * Whether the first mailbox status read has finished, so whether the email is sent is
   * known. Unlike loadingMailboxDraft it never turns back on for a later read.
   */
  const [hasLoadedMailboxStatus, setHasLoadedMailboxStatus] = React.useState(false);
  const [mailboxStatusError, setMailboxStatusError] = React.useState<string | null>(null);
  const [mailboxDraftLoadFailed, setMailboxDraftLoadFailed] = React.useState(false);
  const [mailboxDraft, setMailboxDraft] = React.useState<MailboxDraftFieldsFragment | null>(null);

  const [sending, setSending] = React.useState(false);
  const [cancellingSend, setCancellingSend] = React.useState(false);
  const [clickDeadline, setClickDeadline] = React.useState<string | null>(null);
  const [restoredDeadline, setRestoredDeadline] = React.useState<{
    id: string;
    at: number;
  } | null>(null);
  const [sendError, setSendError] = React.useState<string | null>(null);
  const [sentEmail, setSentEmail] = React.useState<SentEmail | null>(null);
  const [scheduledEmail, setScheduledEmail] = React.useState<SentEmail | null>(null);
  const [deliveryPollingId, setDeliveryPollingId] = React.useState<string | null>(null);
  const sendAfter =
    clickDeadline ??
    (restoredDeadline && restoredDeadline.id === sentEmail?.id
      ? new Date(restoredDeadline.at).toISOString()
      : undefined);

  /**
   * The composition this composer stands behind. Seeded from the artifact it
   * rendered and moved only by its own writes: a revision that arrived from
   * anywhere else describes words this user has not read, and adopting it
   * would be the composer approving an edit on their behalf.
   */
  const revisionRef = React.useRef(contentRevision);
  const wasHydratingRef = React.useRef(!!bodyHydrating);
  React.useEffect(() => {
    // Emptied ticks carry a fingerprint hashed from the empty payload. Once the
    // version fetch lands the stored revision, swap it in — still not a remote
    // edit, just correcting the seed.
    if (wasHydratingRef.current && !bodyHydrating) {
      revisionRef.current = contentRevision;
    }
    wasHydratingRef.current = !!bodyHydrating;
  }, [bodyHydrating, contentRevision]);

  React.useEffect(() => {
    let current = true;

    Promise.all([
      client.query<MailboxAccountsQuery, MailboxAccountsQueryVariables>({
        query: MailboxAccountsDocument,
        fetchPolicy: 'network-only'
      }),
      client.query<MailboxSignaturesQuery, MailboxSignaturesQueryVariables>({
        query: MailboxSignaturesDocument,
        fetchPolicy: 'network-only'
      }),
      // Cached rather than refetched: a ceiling changes with a deploy, not
      // with the draft, and the composer is opened and closed many times a
      // session.
      client.query<MailboxComposeLimitsQuery, MailboxComposeLimitsQueryVariables>({
        query: MailboxComposeLimitsDocument
      })
    ])
      .then(([accountsResult, signaturesResult, limitsResult]) => {
        if (!current) return;
        setMailboxAccounts(accountsResult.data?.mailboxAccounts ?? []);
        setSignatures(signaturesResult.data?.mailboxSignatures ?? []);
        setBodyLimitBytes(limitsResult.data?.mailboxComposeLimits?.bodyBytes ?? null);
        setAttachmentLimitBytes(
          resolveAttachmentLimit(limitsResult.data?.mailboxComposeLimits?.attachmentBytes)
        );
      })
      .catch((err) => {
        if (!current) return;
        // A composer with no mailbox list is still worth showing — the body and
        // recipients are editable and the send button explains why it is off.
        setLoadError(messageFromError(err, 'We could not load your connected mailboxes.'));
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, []);

  const refreshMailboxAccounts = React.useCallback(async () => {
    try {
      const result = await client.query<MailboxAccountsQuery, MailboxAccountsQueryVariables>({
        query: MailboxAccountsDocument,
        fetchPolicy: 'network-only'
      });
      if (result.data?.mailboxAccounts) setMailboxAccounts(result.data.mailboxAccounts);
    } catch {
      // The failed mailbox operation already has a user-facing error. A failed
      // refresh must not replace it or discard the last known account list.
    }
  }, []);

  // A response belongs to one artifact and one request generation. Invalidate
  // reads on both sides of a mutation so an older empty list cannot erase the
  // draft the mutation just created, even if it finishes during that mutation.
  //
  // Held in a ref rather than a memo: React may discard a memoized value and
  // recompute it, which here would hand the in-flight reads a fresh generation
  // counter and let a stale response pass for a current one. Switching artifact
  // retires the outgoing scope instead of resetting this one, so responses in
  // flight for the previous email can never satisfy the new email's checks.
  const mailboxScopeRef = React.useRef<{
    artifactId: string;
    scope: MailboxRequestScope;
  } | null>(null);
  if (mailboxScopeRef.current?.artifactId !== artifactId) {
    // Allocating a scope during render is safe -- a render React discards only
    // leaves an object nobody reads. Retiring the outgoing scope here would
    // not be: a discarded render would have dropped the responses the render
    // that actually commits is still waiting for. So `active` is derived from
    // whether this ref still points at the scope, which no discarded render
    // can get wrong, rather than written down by whichever render ran last.
    const scope: MailboxRequestScope = {
      get active() {
        return scope.mounted && mailboxScopeRef.current?.scope === scope;
      },
      mounted: true,
      generation: 0,
      pendingMutations: 0,
      draft: null,
      send: null
    };
    mailboxScopeRef.current = { artifactId, scope };
  }
  const mailboxScope = mailboxScopeRef.current.scope;
  React.useEffect(() => {
    mailboxScope.mounted = true;
    setMailboxDraft(null);
    setSentEmail(null);
    setScheduledEmail(null);
    setSending(false);
    setCancellingSend(false);
    setClickDeadline(null);
    setRestoredDeadline(null);
    setSavingToMailbox(false);
    setSendError(null);
    setMailboxDraftError(null);
    setMailboxStatusError(null);
    setMailboxDraftLoadFailed(false);
    setHasLoadedMailboxStatus(false);
    return () => {
      mailboxScope.mounted = false;
    };
  }, [mailboxScope]);

  const refreshMailboxStatus = React.useCallback(async (): Promise<MailboxStatus | null> => {
    if (!mailboxScope.active) {
      setHasLoadedMailboxStatus(true);
      return null;
    }
    if (mailboxScope.pendingMutations > 0) return null;
    const generation = ++mailboxScope.generation;
    const current = () => mailboxScope.active && mailboxScope.generation === generation;
    setLoadingMailboxDraft(true);
    const startedAt = performance.now();
    try {
      const [drafts, sends] = await Promise.all([
        client.query<SearchMailboxDraftsQuery, SearchMailboxDraftsQueryVariables>({
          query: SearchMailboxDraftsDocument,
          variables: { filter: { workspaceArtifactId: artifactId } },
          fetchPolicy: 'no-cache',
          context: { queryDeduplication: false }
        }),
        client.query<SearchOutboundEmailsQuery, SearchOutboundEmailsQueryVariables>({
          query: SearchOutboundEmailsDocument,
          variables: { filter: { workspaceArtifactId: artifactId } },
          fetchPolicy: 'no-cache',
          context: { queryDeduplication: false }
        })
      ]);
      if (!current()) return null;
      if (
        !drafts.data?.searchMailboxDrafts ||
        !sends.data?.searchOutboundEmails ||
        drafts.errors?.length ||
        sends.errors?.length
      ) {
        throw new Error('Missing mailbox status');
      }
      const sendRecords = sends.data.searchOutboundEmails.edges.map((edge) => edge.node);
      const status: MailboxStatus = {
        mailboxDraft: drafts.data.searchMailboxDrafts.edges[0]?.node ?? null,
        sentEmail: sendRecords.find(isCommitted) ?? null,
        scheduledEmail: sendRecords.find(isScheduled) ?? null
      };
      mailboxScope.draft = status.mailboxDraft;
      setMailboxDraft(status.mailboxDraft);
      setSentEmail(status.sentEmail);
      setScheduledEmail(status.scheduledEmail);
      if (status.sentEmail?.status === OutboundEmailStatus.Sending) {
        // Deduct the whole read duration conservatively: neither network transit
        // nor waiting for the parallel draft lookup should extend cancellation.
        const remaining = Math.max(
          0,
          Math.min(SEND_CANCELLATION_WINDOW_MS, status.sentEmail.cancellationRemainingMs ?? 0) -
            (performance.now() - startedAt)
        );
        const deadline = { id: status.sentEmail.id, at: Date.now() + remaining };
        setRestoredDeadline((previous) =>
          previous?.id === deadline.id
            ? { ...deadline, at: Math.min(previous.at, deadline.at) }
            : deadline
        );
      } else {
        setClickDeadline(null);
        setRestoredDeadline(null);
        mailboxScope.send = null;
      }
      const latestSend = sends.data.searchOutboundEmails.edges[0]?.node;
      if (latestSend?.status === OutboundEmailStatus.Failed) {
        setSendError(latestSend.failureReason || 'This email could not be sent. Please try again.');
      }
      setMailboxDraftLoadFailed(false);
      setMailboxStatusError(null);
      return status;
    } catch {
      if (current()) {
        setMailboxDraftLoadFailed(true);
        setMailboxStatusError(
          'We could not check the mailbox draft or send status. Retry the check before continuing.'
        );
      }
      return null;
    } finally {
      if (current()) {
        setLoadingMailboxDraft(false);
        setHasLoadedMailboxStatus(true);
      }
    }
  }, [artifactId, mailboxScope]);

  // Keyed on this email rather than on the workspace clock: workspaceUpdatedAt
  // moves for every message and every other artifact in the workspace, and each
  // tick cost two uncached reads about an email nothing had touched. What can
  // change the answer is this artifact's own version, a switch of artifact
  // (refreshMailboxStatus is rebuilt), or Cara finishing its turn.
  //
  // The two reads per tick are affordable because a version is a committed
  // edit -- a recipient, the subject, an attachment, the signature, the sender
  // -- and not a keystroke: the body editor does not save as it is typed.
  React.useEffect(() => {
    if (workspaceState !== CaraWorkspaceState.Working) void refreshMailboxStatus();
  }, [refreshMailboxStatus, workspaceState, version]);

  const beginMailboxMutation = React.useCallback(() => {
    mailboxScope.pendingMutations += 1;
    mailboxScope.generation += 1;
    setLoadingMailboxDraft(false);
    return () => {
      mailboxScope.pendingMutations -= 1;
      mailboxScope.generation += 1;
    };
  }, [mailboxScope]);

  /**
   * A mailbox whose grant has died cannot send, so it is never offered as the
   * from-address even though it stays visible in settings.
   */
  const sendableAccounts = React.useMemo(
    () => mailboxAccounts.filter((account) => account.status === MailboxAccountStatus.Connected),
    [mailboxAccounts]
  );

  /**
   * Mailboxes the user connected that Cara can no longer reach. Named to explain
   * an empty From list, and still refused as a from-address.
   */
  const reconnectAccounts = React.useMemo(
    () =>
      mailboxAccounts.filter((account) => account.status === MailboxAccountStatus.NeedsReconnect),
    [mailboxAccounts]
  );

  const hasScheduledEmail = scheduledEmail !== null;
  const writeDraft = React.useCallback(
    async (email: CaraWorkspaceArtifactEmailInput): Promise<boolean> => {
      if (!workspaceId || !artifactId || !version) {
        return false;
      }

      setSaveStatus('saving');
      setSaveError(null);
      try {
        const result = await client.mutate<
          UpdateCaraWorkspaceArtifactMutation,
          UpdateCaraWorkspaceArtifactMutationVariables
        >({
          mutation: UpdateCaraWorkspaceArtifactDocument,
          variables: {
            workspaceId,
            artifactId,
            version,
            // Naming the revision being written over is what stops this save
            // from silently replacing an edit made in another tab, and then
            // sending the result as if it had been read.
            input: { email: { ...email, expectedContentRevision: revisionRef.current } }
          }
        });
        const saved = result.data?.updateCaraWorkspaceArtifact;
        // A refused write resolves like any other — null field, reason in errors.
        if (!saved) {
          setSaveStatus('error');
          setSaveError(messageFromResult(result, 'We could not save this draft.'));
          // The scheduled email may have started sending before this save, so
          // the refresh shows it sending and locks the fields.
          if (hasScheduledEmail) await refreshMailboxStatus();
          return false;
        }
        if (saved.email?.contentRevision) {
          revisionRef.current = saved.email.contentRevision;
        }
        setSaveStatus('saved');
        return true;
      } catch (err) {
        setSaveStatus('error');
        setSaveError(messageFromError(err, 'We could not save this draft.'));
        return false;
      }
    },
    [workspaceId, artifactId, version, hasScheduledEmail, refreshMailboxStatus]
  );

  /**
   * Saves run one at a time. Each one declares the revision it is writing
   * over, and that revision is only known once the save before it has come
   * back — two saves in flight would race to describe the same composition,
   * and the loser would be refused as though a stranger had written it.
   */
  const queue = React.useRef<Promise<boolean>>(Promise.resolve(true));
  const save = React.useCallback(
    (email: CaraWorkspaceArtifactEmailInput): Promise<boolean> => {
      const next = queue.current.then(() => writeDraft(email));
      queue.current = next;
      return next;
    },
    [writeDraft]
  );

  /**
   * Puts a file in the workspace and reports back what the composer needs to show
   * it. Resolves null when nothing was stored — uploadError already says why, and
   * the draft is left untouched rather than pointed at a file that does not exist.
   * The workspace owns the bytes, so a detached file is still findable there.
   */
  const uploadAttachment = React.useCallback(
    async (file: File): Promise<AttachmentFile | null> => {
      const pending: PendingUpload = {
        key: `upload-${(pendingUploadCounter += 1)}`,
        name: file.name,
        size: file.size
      };
      setUploading((current) => [...current, pending]);
      setUploadError(null);
      try {
        const result = await client.mutate<
          UploadEmailAttachmentMutation,
          UploadEmailAttachmentMutationVariables
        >({
          mutation: UploadEmailAttachmentDocument,
          variables: {
            workspaceId,
            file: {
              file,
              type: FileType.Other,
              visible: true,
              notes: `Attached to email artifact ${artifactId}`
            }
          }
        });
        const uploaded = result.data?.uploadWorkspaceFile;
        if (!uploaded) {
          setUploadError(`We could not upload “${file.name}”. Please try again.`);
          return null;
        }
        setUploads((current) => [...current, uploaded]);
        return uploaded;
      } catch (err) {
        setUploadError(messageFromError(err, `We could not upload “${file.name}”.`));
        return null;
      } finally {
        setUploading((current) => current.filter((entry) => entry.key !== pending.key));
      }
    },
    [workspaceId, artifactId]
  );

  /**
   * The documents this email could carry. Every workspace the sender can read is
   * in scope, not just this one, and the server decides what they may see.
   */
  const searchAttachableArtifacts = React.useCallback(
    (search: string): Promise<AttachableArtifact[]> =>
      readAttachableArtifacts({ search, limit: ATTACHABLE_ARTIFACT_LIMIT }),
    []
  );

  /**
   * Names the artifacts a stored draft already carries. Asks by ID with no
   * `limit`: the named IDs are the result set, and capping it would drop names
   * the composer then treats as missing. An ID absent from the answer is one the
   * sender may no longer read, which is the same rule the send is refused under.
   */
  const resolveAttachedArtifacts = React.useCallback(
    async (artifactIds: string[]): Promise<AttachableArtifact[]> => {
      if (artifactIds.length === 0) {
        return [];
      }
      const batches: string[][] = [];
      for (let start = 0; start < artifactIds.length; start += ATTACHED_ARTIFACT_ID_LIMIT) {
        batches.push(artifactIds.slice(start, start + ATTACHED_ARTIFACT_ID_LIMIT));
      }
      const named = await Promise.all(batches.map((ids) => readAttachableArtifacts({ ids })));
      // Matched back by ID rather than by position: the server answers
      // most-recently-updated first, in nothing like the order it was asked.
      const wanted = new Set(artifactIds);
      return named.flat().filter((artifact) => wanted.has(artifact.id));
    },
    []
  );

  /**
   * Writes the stored draft into the chosen mailbox, where it waits in the
   * Drafts folder until someone sends it from their own mail client. Pins the
   * same version and composition the send does: a draft parked in a mailbox is
   * one keystroke from going out, so it may not carry words nobody read.
   *
   * Safe to call again — the server updates the copy already in the mailbox
   * rather than adding a second one — so there is no confirm step in front of
   * it.
   */
  const saveToMailbox = React.useCallback(
    async (mailboxAccountId: string): Promise<boolean> => {
      if (!mailboxScope.active) return false;
      const finishMutation = beginMailboxMutation();
      setSavingToMailbox(true);
      setMailboxDraftError(null);
      const mailClient = mailClientName(
        mailboxAccounts.find((account) => account.id === mailboxAccountId)?.provider
      );
      let failure = null as MailboxActionError | null;
      const showError = async (error: unknown, fallback: string) => {
        failure = mailboxActionError(error, fallback, mailClient);
        setMailboxDraftError(failure.message);
        if (failure.requiresAccountRefresh) await refreshMailboxAccounts();
      };
      try {
        const result = await client.mutate<
          CreateMailboxDraftMutation,
          CreateMailboxDraftMutationVariables
        >({
          mutation: CreateMailboxDraftDocument,
          variables: {
            mailboxAccountId,
            artifactId,
            artifactVersion: version,
            contentRevision: revisionRef.current
          }
        });
        if (!mailboxScope.active) return false;
        const draft = result.data?.createMailboxDraft;
        if (!draft) {
          await showError(
            { graphQLErrors: result.errors ?? [] },
            'The draft did not reach your mailbox. Please try again.'
          );
          return false;
        }
        mailboxScope.draft = draft;
        setMailboxDraft(draft);
        return true;
      } catch (err) {
        if (!mailboxScope.active) return false;
        await showError(err, 'We could not save this email to your mailbox.');
        return false;
      } finally {
        finishMutation();
        if (mailboxScope.active) {
          setSavingToMailbox(false);
          // The server recorded the send from the mail client, so the refresh
          // shows this email sent and locks it.
          if (failure?.sentExternally) await refreshMailboxStatus();
        }
      }
    },
    [
      artifactId,
      version,
      mailboxAccounts,
      refreshMailboxAccounts,
      refreshMailboxStatus,
      beginMailboxMutation,
      mailboxScope
    ]
  );

  const switchMailboxDraft = React.useCallback(
    async (nextMailboxAccountId: string, createDraft: boolean): Promise<boolean> => {
      const draft = mailboxScope.draft;
      if (!draft) return false;
      if (!mailboxScope.active) return false;
      const finishMutation = beginMailboxMutation();
      setMailboxDraftError(null);
      try {
        const result = await client.mutate<
          SwitchMailboxDraftMutation,
          SwitchMailboxDraftMutationVariables
        >({
          mutation: SwitchMailboxDraftDocument,
          variables: {
            input: {
              createDraft,
              mailboxDraftId: draft.id,
              nextMailboxAccountId,
              artifactId,
              artifactVersion: version,
              contentRevision: revisionRef.current
            }
          }
        });
        if (!mailboxScope.active) return false;
        const switched = result.data?.switchMailboxDraft;
        if (!switched) {
          setMailboxDraftError(messageFromResult(result, SWITCH_UNCONFIRMED));
          if (requiresMailboxRefresh({ graphQLErrors: result.errors })) {
            await refreshMailboxAccounts();
          }
          return false;
        }
        if (switched.artifact.email?.contentRevision) {
          revisionRef.current = switched.artifact.email.contentRevision;
        }
        mailboxScope.draft = switched.mailboxDraft ?? null;
        setMailboxDraft(mailboxScope.draft);
        setMailboxDraftError(switched.draftError ?? null);
        return true;
      } catch (err) {
        if (!mailboxScope.active) return false;
        setMailboxDraftError(messageFromError(err, SWITCH_UNCONFIRMED));
        if (requiresMailboxRefresh(err)) {
          await refreshMailboxAccounts();
        }
        return false;
      } finally {
        finishMutation();
      }
    },
    [artifactId, version, refreshMailboxAccounts, beginMailboxMutation, mailboxScope]
  );

  const scheduled = useScheduledEmail({
    artifactId,
    version,
    mailboxScope,
    mailboxAccounts,
    revisionRef,
    queue,
    scheduledEmail,
    setScheduledEmail,
    beginMailboxMutation,
    refreshMailboxStatus
  });
  const { clearScheduleError } = scheduled;

  /**
   * The send itself. Pins both the version and the composition the user was
   * looking at: the server refuses a send whose artifact has moved on, so what
   * was reviewed is what goes out. Resolves true only when the mutation
   * succeeded.
   *
   * Shares the save queue so an in-flight autosave cannot invalidate the send's
   * revision. Later saves also wait for the revision the accepted send returns.
   */
  const send = React.useCallback(
    (mailboxAccountId: string, email: CaraWorkspaceArtifactEmailInput = {}): Promise<boolean> => {
      if (!mailboxScope.active || (mailboxScope.send && !mailboxScope.send.completed))
        return Promise.resolve(false);
      const attempt = {
        id: crypto.randomUUID(),
        deadline: Date.now() + SEND_CANCELLATION_WINDOW_MS,
        cancelled: false,
        started: false,
        completed: false
      };
      mailboxScope.send = attempt;
      setClickDeadline(new Date(attempt.deadline).toISOString());
      // Lock the composer while waiting, not just while the send request is running.
      const finishMutation = beginMailboxMutation();
      setSending(true);
      setSendError(null);
      clearScheduleError();
      const mailClient = mailClientName(
        mailboxAccounts.find((account) => account.id === mailboxAccountId)?.provider
      );
      let failure = null as MailboxActionError | null;
      const showError = async (error: unknown, fallback: string) => {
        failure = mailboxActionError(error, fallback, mailClient);
        setSendError(failure.message);
        if (failure.requiresAccountRefresh) await refreshMailboxAccounts();
      };
      // Never rejects: a refused or failed send resolves false, so the queue
      // behind it keeps moving.
      const schedule = async (): Promise<boolean> => {
        try {
          // Cancel may have completed while this click waited behind an autosave.
          if (attempt.cancelled) return false;
          attempt.started = true;
          const result = await client.mutate<
            ScheduleMailboxEmailMutation,
            ScheduleMailboxEmailMutationVariables
          >({
            mutation: ScheduleMailboxEmailDocument,
            variables: {
              input: {
                attemptId: attempt.id,
                cancellationWindowMs: Math.min(
                  SEND_CANCELLATION_WINDOW_MS,
                  Math.max(0, attempt.deadline - Date.now())
                ),
                mailboxAccountId,
                artifactId,
                artifactVersion: version,
                contentRevision: revisionRef.current,
                email
              }
            },
            context: {
              fetchOptions: {
                keepalive: new Blob([JSON.stringify(email)]).size < KEEPALIVE_EMAIL_BODY_LIMIT_BYTES
              }
            }
          });
          if (!mailboxScope.active) return false;
          const scheduled = result.data?.scheduleMailboxEmail;
          const sent = scheduled?.email;
          if (!scheduled || !sent) {
            if (attempt.cancelled) return false;
            mailboxScope.send = null;
            setClickDeadline(null);
            await showError(
              { graphQLErrors: result.errors ?? [] },
              'The email could not be queued. Please try again.'
            );
            return false;
          }
          revisionRef.current = scheduled.contentRevision;
          if (attempt.cancelled || sent.status === OutboundEmailStatus.Cancelled) {
            setSentEmail(null);
            setClickDeadline(null);
          } else {
            setSentEmail(sent);
          }
          return true;
        } catch (err) {
          if (!mailboxScope.active || attempt.cancelled) return false;
          mailboxScope.send = null;
          setClickDeadline(null);
          await showError(err, 'We could not queue this email.');
          return false;
        } finally {
          attempt.completed = true;
          finishMutation();
          if (mailboxScope.active) {
            setSending(false);
            setCancellingSend(false);
            // The server recorded the send from the mail client, so the refresh
            // shows this email sent and locks it.
            if (attempt.cancelled || failure?.sentExternally) await refreshMailboxStatus();
          }
        }
      };
      const next = queue.current.then(schedule);
      queue.current = next;
      return next;
    },
    [
      artifactId,
      version,
      mailboxAccounts,
      refreshMailboxAccounts,
      refreshMailboxStatus,
      beginMailboxMutation,
      mailboxScope,
      clearScheduleError
    ]
  );

  const cancelSend = React.useCallback(async (): Promise<void> => {
    const attempt = mailboxScope.send;
    if ((!sentEmail && !attempt) || !mailboxScope.active) return;
    const deadline = attempt?.deadline ?? (sendAfter ? new Date(sendAfter).getTime() : 0);
    if (deadline <= Date.now()) return;
    const finishMutation = beginMailboxMutation();
    setSendError(null);
    try {
      if (attempt) {
        // A click still waiting behind autosave has not authorized a server
        // request; stopping it locally is enough. Once sent, require an ack.
        while (attempt.started) {
          const result = await client.mutate<
            CancelMailboxEmailPreparationMutation,
            CancelMailboxEmailPreparationMutationVariables
          >({
            mutation: CancelMailboxEmailPreparationDocument,
            variables: { artifactId, artifactVersion: version, attemptId: attempt.id },
            context: { fetchOptions: { keepalive: true } }
          });
          if (!mailboxScope.active || mailboxScope.send !== attempt) return;
          if (result.data?.cancelMailboxEmailPreparation === true) break;
          if (
            result.data?.cancelMailboxEmailPreparation !== false ||
            result.errors?.length ||
            Date.now() + CANCELLATION_RETRY_MS >= deadline
          ) {
            setSendError(
              messageFromResult(result, 'We could not cancel this send. It may still be sent.')
            );
            return;
          }
          await new Promise((resolve) => window.setTimeout(resolve, CANCELLATION_RETRY_MS));
          if (!mailboxScope.active || mailboxScope.send !== attempt) return;
          if (Date.now() >= deadline) {
            setSendError('We could not cancel this send. It may still be sent.');
            return;
          }
        }
        attempt.cancelled = true;
        setCancellingSend(!attempt.completed);
        setClickDeadline(null);
        setSentEmail(null);
        return;
      }
      if (!sentEmail) return;
      const result = await client.mutate<
        CancelMailboxEmailMutation,
        CancelMailboxEmailMutationVariables
      >({
        mutation: CancelMailboxEmailDocument,
        variables: { id: sentEmail.id },
        context: { fetchOptions: { keepalive: true } }
      });
      if (!mailboxScope.active) return;
      if (result.data?.cancelMailboxEmail.status !== OutboundEmailStatus.Cancelled) {
        setSendError(
          messageFromResult(result, 'We could not cancel this send. It may still be sent.')
        );
        return;
      }
      setSentEmail(null);
    } catch (err) {
      if (mailboxScope.active)
        setSendError(messageFromError(err, 'We could not cancel this send. It may still be sent.'));
    } finally {
      finishMutation();
      if (mailboxScope.active) await refreshMailboxStatus();
    }
  }, [
    artifactId,
    version,
    sentEmail,
    sendAfter,
    mailboxScope,
    refreshMailboxStatus,
    beginMailboxMutation
  ]);

  // Watched from the moment its delivery can start until it resolves.
  const delivery = awaitedDelivery({ sentEmail, scheduledEmail }, sendAfter);
  const awaitedDeliveryId = delivery?.id ?? null;
  const deliveryStartsAtMs = delivery?.startsAt ? new Date(delivery.startsAt).getTime() : 0;

  React.useEffect(() => {
    if (!awaitedDeliveryId) {
      setDeliveryPollingId(null);
      return;
    }
    // Delivery cannot start before the deadline; the countdown is painted locally.
    const remaining = Math.max(0, deliveryStartsAtMs - Date.now());
    if (remaining === 0) {
      setDeliveryPollingId(awaitedDeliveryId);
      return;
    }
    setDeliveryPollingId(null);
    if (remaining > LONGEST_TIMER_DELAY_MS) return;
    const timer = window.setTimeout(() => setDeliveryPollingId(awaitedDeliveryId), remaining);
    return () => window.clearTimeout(timer);
  }, [awaitedDeliveryId, deliveryStartsAtMs]);

  // Clearing sendAfter when the worker claims delivery must not reset the backoff.
  React.useEffect(() => {
    if (!deliveryPollingId || deliveryPollingId !== awaitedDeliveryId) return;
    let active = true;
    let timer: number | undefined;
    let delay = DELIVERY_POLL_INITIAL_DELAY_MS;
    const stopAt = Date.now() + DELIVERY_POLL_DURATION_MS;
    const poll = async () => {
      if (!active || Date.now() >= stopAt) return;
      const status = await refreshMailboxStatus();
      const isStillAwaited = !status || !!awaitedDelivery(status);
      if (!active || !isStillAwaited || Date.now() + delay >= stopAt) return;
      timer = window.setTimeout(() => void poll(), delay);
      delay = Math.min(delay * 2, DELIVERY_POLL_MAX_DELAY_MS);
    };
    void poll();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [deliveryPollingId, awaitedDeliveryId, refreshMailboxStatus]);

  React.useEffect(() => {
    if (!awaitedDeliveryId) return;
    const refresh = () => void refreshMailboxStatus();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [awaitedDeliveryId, refreshMailboxStatus]);

  return {
    mailboxAccounts,
    sendableAccounts,
    reconnectAccounts,
    signatures,
    bodyLimitBytes,
    attachmentLimitBytes,
    loading,
    loadError,
    save,
    saveStatus,
    saveError,
    uploadAttachment,
    searchAttachableArtifacts,
    resolveAttachedArtifacts,
    uploads,
    uploading,
    uploadError,
    clearUploadError: React.useCallback(() => setUploadError(null), []),
    saveToMailbox,
    savingToMailbox,
    mailboxDraft,
    mailboxDraftError: mailboxStatusError ?? mailboxDraftError,
    refreshMailboxStatus,
    loadingMailboxDraft,
    hasLoadedMailboxStatus,
    mailboxDraftLoadFailed,
    switchMailboxDraft,
    send,
    cancelSend,
    sending,
    cancellingSend,
    sendAfter,
    sendError,
    sentEmail,
    clearSendError: React.useCallback(() => setSendError(null), []),
    scheduledEmail,
    ...scheduled
  };
};
